/**
 * @fileoverview Componente responsável por orquestrar a experiência visual do construtor de fluxos.
 */

import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  IconButton, Tooltip, Switch, CircularProgress,
} from "@material-ui/core";
import {
  ArrowLeft, Save, Power, GripVertical, Check,
} from "lucide-react";
import ReactFlow, {
  Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState,
  MarkerType, ReactFlowProvider, useReactFlow,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";

import api from "../../../services/api";
import openSocket from "../../../services/socket-io";
import toastError from "../../../errors/toastError";
import { toast } from "react-toastify";

import useStyles from "./styles";
import FlowNode from "./FlowNode";
import LogPanel from "./LogPanel";
import ConfigPanel from "./panels/ConfigPanel";
import PromptModal, { MemoryPromptModal } from "./panels/PromptModal";
import useWorkflowSocket from "./hooks/useWorkflowSocket";
import {
  SIDEBAR_CATEGORIES, NODE_TYPES_CONFIG, DEFAULT_NODES,
} from "./constants";

var nodeTypes = { flowNode: FlowNode };

/* ─── Inner component (needs ReactFlowProvider) ──────── */
// Constantes de largura da sidebar redimensionável
var SIDEBAR_DEFAULT_WIDTH = 220;
var SIDEBAR_MIN_WIDTH = 180;
var SIDEBAR_MAX_WIDTH = 420;
var SIDEBAR_STORAGE_KEY = "flowBuilder:sidebarWidth";

var readSidebarWidth = function () {
  try {
    var raw = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (!raw) return SIDEBAR_DEFAULT_WIDTH;
    var n = parseInt(raw, 10);
    if (isNaN(n)) return SIDEBAR_DEFAULT_WIDTH;
    return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, n));
  } catch (e) {
    return SIDEBAR_DEFAULT_WIDTH;
  }
};

var FlowBuilderInner = function () {
  var classes = useStyles();
  var history = useHistory();
  var params = useParams();
  var id = params.id;
  var reactFlowWrapper = useRef(null);
  var rfInstance = useReactFlow();

  // Sidebar redimensionável
  var sidebarRef = useRef(null);
  var logPanelRef = useRef(null);
  var sidebarWidthState = useState(function () { return readSidebarWidth(); });
  var sidebarWidth = sidebarWidthState[0];
  var setSidebarWidth = sidebarWidthState[1];
  var draggingRef = useRef(false);
  var dragStartRef = useRef({ x: 0, w: 0 });

  var nodesState = useNodesState([]);
  var nodes = nodesState[0];
  var setNodes = nodesState[1];
  var onNodesChange = nodesState[2];

  var edgesState = useEdgesState([]);
  var edges = edgesState[0];
  var setEdges = edgesState[1];
  var onEdgesChange = edgesState[2];

  var titleState = useState("");
  var title = titleState[0];
  var setTitle = titleState[1];

  var activeState = useState(false);
  var isActive = activeState[0];
  var setIsActive = activeState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var savedState = useState(false);
  var saved = savedState[0];
  var setSaved = savedState[1];

  var dirtyState = useState(false);
  var dirty = dirtyState[0];
  var setDirty = dirtyState[1];

  var selectedNodeState = useState(null);
  var selectedNodeId = selectedNodeState[0];
  var setSelectedNodeId = selectedNodeState[1];

  var queuesState = useState([]);
  var queues = queuesState[0];
  var setQueues = queuesState[1];

  var whatsappsState = useState([]);
  var whatsapps = whatsappsState[0];
  var setWhatsapps = whatsappsState[1];

  var leadStatusesState = useState([]);
  var leadStatuses = leadStatusesState[0];
  var setLeadStatuses = leadStatusesState[1];

  var crmBoardsState = useState([]);
  var crmBoards = crmBoardsState[0];
  var setCrmBoards = crmBoardsState[1];

  var usersState = useState([]);
  var users = usersState[0];
  var setUsers = usersState[1];

  var wooIntegrationsState = useState([]);
  var wooIntegrations = wooIntegrationsState[0];
  var setWooIntegrations = wooIntegrationsState[1];

  var saveTimer = useRef(null);
  var hasLoaded = useRef(false);

  var promptModalState = useState(false);
  var promptModalOpen = promptModalState[0];
  var setPromptModalOpen = promptModalState[1];

  /* ── File upload state (tool_files) ──────────── */
  var fileUploadingState = useState(false);
  var fileUploading = fileUploadingState[0];
  var setFileUploading = fileUploadingState[1];

  var pendingFileNameState = useState("");
  var pendingFileName = pendingFileNameState[0];
  var setPendingFileName = pendingFileNameState[1];

  var pendingFileState = useState(null);
  var pendingFile = pendingFileState[0];
  var setPendingFile = pendingFileState[1];

  /* ── Memory state (tool_memory) ──────────── */
  var memNameState = useState("");
  var memName = memNameState[0];
  var setMemName = memNameState[1];

  var memContentState = useState("");
  var memContent = memContentState[0];
  var setMemContent = memContentState[1];

  var memSummaryState = useState("");
  var memSummary = memSummaryState[0];
  var setMemSummary = memSummaryState[1];

  var memGeneratingState = useState(false);
  var memGenerating = memGeneratingState[0];
  var setMemGenerating = memGeneratingState[1];

  var memEditingIdState = useState(null);
  var memEditingId = memEditingIdState[0];
  var setMemEditingId = memEditingIdState[1];

  var memExpandedIdState = useState(null);
  var memExpandedId = memExpandedIdState[0];
  var setMemExpandedId = memExpandedIdState[1];

  /* ── CRM field state (tool_crm_create) ──── */
  var crmFieldNameState = useState("");
  var crmFieldName = crmFieldNameState[0];
  var setCrmFieldName = crmFieldNameState[1];

  /* ── Memory prompt modal (expand) ──────── */
  var memPromptModalState = useState(false);
  var memPromptModalOpen = memPromptModalState[0];
  var setMemPromptModalOpen = memPromptModalState[1];

  /* ── Template state (send_template) ──── */
  var bulkTemplatesState = useState([]);
  var bulkTemplates = bulkTemplatesState[0];
  var setBulkTemplates = bulkTemplatesState[1];

  var loadingTemplatesState = useState(false);
  var loadingTemplates = loadingTemplatesState[0];
  var setLoadingTemplates = loadingTemplatesState[1];

  var lastTemplateWhatsappId = useRef(null);

  /* ── Bulk send state ──────────────────────── */
  var bulkProgressState = useState(null);
  var bulkProgress = bulkProgressState[0];
  var setBulkProgress = bulkProgressState[1];

  var bulkSendingState = useState(false);
  var bulkSending = bulkSendingState[0];
  var setBulkSending = bulkSendingState[1];

  /* ── Execution socket ──────────────────────── */
  var socketData = useWorkflowSocket(id);
  var executionLogs = socketData.executionLogs;
  var logPanelOpen = socketData.logPanelOpen;
  var setLogPanelOpen = socketData.setLogPanelOpen;
  var nodeExecution = socketData.nodeExecution;

  /* ── Load queues & whatsapps ─────────────────── */
  useEffect(function () {
    api.get("/queue").then(function (res) { setQueues(res.data); }).catch(function () {});
    api.get("/whatsapp/?session=0").then(function (res) { setWhatsapps(res.data); }).catch(function () {});
    api.get("/lead-statuses").then(function (res) { setLeadStatuses(res.data); }).catch(function () {});
    api.get("/crm-boards").then(function (res) { setCrmBoards(res.data); }).catch(function () {});
    api.get("/users/list").then(function (res) { setUsers(res.data); }).catch(function () {});
  }, []);

  /* ── Lazy-load integrações WooCommerce ──────────
     Só busca quando o fluxo tem pelo menos um node tool_woocommerce.
     Fluxos sem WooCommerce evitam um GET /integrations no mount. */
  var wooLoaded = useRef(false);
  useEffect(function () {
    if (wooLoaded.current) return;
    var hasWooNode = nodes.some(function (n) {
      return n.data && n.data.nodeType === "tool_woocommerce";
    });
    if (!hasWooNode) return;
    wooLoaded.current = true;
    api.get("/integrations?type=woocommerce")
      .then(function (res) { setWooIntegrations(res.data || []); })
      .catch(function () { wooLoaded.current = false; });
  }, [nodes]);

  /* ── Bulk progress socket listener ─────────── */
  useEffect(function () {
    var companyId = localStorage.getItem("companyId");
    if (!companyId || !id) return;
    var socket = openSocket();
    var handleBulkProgress = function (data) {
      if (String(data.workflowId) !== String(id)) return;
      setBulkProgress(data);
      if (data.done) {
        setBulkSending(false);
        toast.success("Disparo concluido: " + data.sent + " enviados" + (data.failed > 0 ? ", " + data.failed + " falhas" : ""));
      }
    };
    socket.on("company-" + companyId + "-bulkProgress", handleBulkProgress);

    // Listener para stats de delivered/read/clicks vindas do webhook
    var handleWorkflowStats = function (data) {
      if (String(data.workflowId) !== String(id)) return;
      setNodes(function (prevNodes) {
        return prevNodes.map(function (n) {
          if (n.id === data.templateNodeId && data.stats) {
            return {
              ...n,
              data: { ...n.data, stats: data.stats }
            };
          }
          return n;
        });
      });
    };
    socket.on("company-" + companyId + "-workflowStats", handleWorkflowStats);

    return function () {
      socket.off("company-" + companyId + "-bulkProgress", handleBulkProgress);
      socket.off("company-" + companyId + "-workflowStats", handleWorkflowStats);
    };
  }, [id]);

  /* ── Selected node data helpers ─────────────── */
  var selectedNode = nodes.find(function (n) { return n.id === selectedNodeId; });

  /* ── Load templates when send_template node selected ── */
  useEffect(function () {
    if (!selectedNode || selectedNode.data.nodeType !== "send_template") {
      lastTemplateWhatsappId.current = null;
      return;
    }
    // Find connected bulk_message node to get whatsappId
    var incomingEdge = edges.find(function (e) { return e.target === selectedNode.id; });
    if (!incomingEdge) { setBulkTemplates([]); return; }
    var sourceNode = nodes.find(function (n) { return n.id === incomingEdge.source; });
    // Walk upstream: source might be bulk_message, or we traverse further
    var bulkNode = null;
    if (sourceNode && sourceNode.data.nodeType === "bulk_message") {
      bulkNode = sourceNode;
    }
    if (!bulkNode || !bulkNode.data.whatsappId) { setBulkTemplates([]); return; }
    var waId = bulkNode.data.whatsappId;
    if (lastTemplateWhatsappId.current === waId) return;
    lastTemplateWhatsappId.current = waId;
    setLoadingTemplates(true);
    api.get("/meta/templates/" + waId)
      .then(function (res) { setBulkTemplates(res.data.templates || res.data || []); })
      .catch(function () { setBulkTemplates([]); })
      .finally(function () { setLoadingTemplates(false); });
  }, [selectedNode, edges, nodes]);

  /* ── Inject execution status into nodes for visual feedback ── */
  useEffect(function () {
    setNodes(function (nds) {
      return nds.map(function (n) {
        var status = nodeExecution[n.id];
        if (status && n.data._executionStatus !== status) {
          return Object.assign({}, n, { data: Object.assign({}, n.data, { _executionStatus: status }) });
        }
        if (!status && n.data._executionStatus) {
          var clean = Object.assign({}, n.data);
          delete clean._executionStatus;
          return Object.assign({}, n, { data: clean });
        }
        return n;
      });
    });
  }, [nodeExecution, setNodes]);

  var updateNodeData = function (key, value) {
    setNodes(function (nds) {
      return nds.map(function (n) {
        if (n.id !== selectedNodeId) return n;
        var newData = Object.assign({}, n.data);
        newData[key] = value;
        return Object.assign({}, n, { data: newData });
      });
    });
    markDirty();
  };

  /* ── Load ─────────────────────────────────────── */
  useEffect(function () {
    var cancelled = false;
    var load = function () {
      api.get("/workflows/" + id)
        .then(function (res) {
          if (cancelled) return;
          var data = res.data;
          setTitle(data.title);
          setIsActive(data.isActive);

          var loadedNodes = data.nodes && data.nodes.length > 0 ? data.nodes : DEFAULT_NODES;
          setNodes(loadedNodes);
          setEdges(data.edges || []);
          hasLoaded.current = true;
        })
        .catch(function (err) {
          if (!cancelled) {
            toastError(err);
            history.push("/fluxo");
          }
        })
        .finally(function () {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    return function () { cancelled = true; };
  }, [id, history, setNodes, setEdges]);

  var markDirty = useCallback(function () {
    if (!hasLoaded.current) return;
    setDirty(true);
    setSaved(false);
  }, []);

  var doSave = useCallback(function (currentNodes, currentEdges, currentTitle, currentActive) {
    setSaving(true);
    api.put("/workflows/" + id, {
      title: currentTitle,
      isActive: currentActive,
      nodes: currentNodes,
      edges: currentEdges,
    })
      .then(function () {
        setDirty(false);
        setSaved(true);
        setTimeout(function () { setSaved(false); }, 2000);
      })
      .catch(function (err) {
        toastError(err);
        setIsActive(function (cur) { return cur !== currentActive ? cur : !currentActive; });
        setDirty(false);
      })
      .finally(function () { setSaving(false); });
  }, [id]);

  /* ── Bulk send handler ──────────────────────── */
  var handleBulkSend = useCallback(function () {
    if (bulkSending) return;
    // Salvar antes de disparar
    doSave(nodes, edges, title, isActive);
    setBulkSending(true);
    setBulkProgress(null);
    api.post("/workflows/" + id + "/bulk-send")
      .then(function (res) {
        toast.info("Disparo iniciado: " + res.data.total + " contatos");
      })
      .catch(function (err) {
        setBulkSending(false);
        toastError(err);
      });
  }, [id, nodes, edges, title, isActive, bulkSending, doSave]);

  /* ── Auto-save ────────────────────────────────── */
  useEffect(function () {
    if (!dirty || !hasLoaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(function () {
      doSave(nodes, edges, title, isActive);
    }, 1500);
    return function () {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [nodes, edges, title, isActive, dirty, doSave]);

  /* ── Connect ──────────────────────────────────── */
  var onConnect = useCallback(function (params) {
    var isToolEdge = params.sourceHandle === "tools";
    var edgeStyle = isToolEdge
      ? { stroke: "#8b5cf6", strokeWidth: 2, strokeDasharray: "6 3" }
      : { stroke: "#5B7CFF", strokeWidth: 2 };
    var edgeColor = isToolEdge ? "#8b5cf6" : "#5B7CFF";

    setEdges(function (eds) {
      return addEdge(
        Object.assign({}, params, {
          type: "smoothstep",
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor },
          style: edgeStyle,
        }),
        eds
      );
    });
    markDirty();
  }, [setEdges, markDirty]);

  /* ── Drag & drop ──────────────────────────────── */
  var onDragOver = useCallback(function (e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  var onDrop = useCallback(function (e) {
    e.preventDefault();
    var nodeType = e.dataTransfer.getData("application/reactflow");
    if (!nodeType || !reactFlowWrapper.current) return;

    var bounds = reactFlowWrapper.current.getBoundingClientRect();
    var position = rfInstance.project({
      x: e.clientX - bounds.left,
      y: e.clientY - bounds.top,
    });

    var cfg = NODE_TYPES_CONFIG.find(function (n) { return n.type === nodeType; });
    var newNode = {
      id: nodeType + "-" + Date.now(),
      type: "flowNode",
      position: position,
      data: { label: cfg ? cfg.label : nodeType, nodeType: nodeType },
    };

    setNodes(function (nds) { return nds.concat(newNode); });
    markDirty();
  }, [rfInstance, setNodes, markDirty]);

  var onDragStart = function (e, nodeType) {
    e.dataTransfer.setData("application/reactflow", nodeType);
    e.dataTransfer.effectAllowed = "move";
  };

  /* ── Add tool from "+" button ───────────────── */
  useEffect(function () {
    var handler = function (e) {
      var detail = e.detail;
      var agentNode = nodes.find(function (n) { return n.id === detail.agentNodeId; });
      if (!agentNode) return;

      var toolCfg = NODE_TYPES_CONFIG.find(function (n) { return n.type === detail.toolType; });
      if (!toolCfg) return;

      var newNode = {
        id: detail.toolType + "-" + Date.now(),
        type: "flowNode",
        position: {
          x: agentNode.position.x,
          y: agentNode.position.y + 160,
        },
        data: { label: toolCfg.label, nodeType: detail.toolType },
      };

      setNodes(function (nds) { return nds.concat(newNode); });

      var newEdge = {
        id: "tools-" + Date.now(),
        source: detail.agentNodeId,
        sourceHandle: "tools",
        target: newNode.id,
        targetHandle: "input",
        type: "smoothstep",
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed, color: "#8b5cf6" },
        style: { stroke: "#8b5cf6", strokeWidth: 2, strokeDasharray: "6 3" },
      };

      setEdges(function (eds) { return eds.concat(newEdge); });
      markDirty();
    };

    window.addEventListener("flow:addTool", handler);
    return function () { window.removeEventListener("flow:addTool", handler); };
  }, [nodes, setNodes, setEdges, markDirty]);

  var handleNodesChange = useCallback(function (changes) {
    onNodesChange(changes);
    var hasMoved = changes.some(function (c) { return c.type === "position" && c.dragging === false; });
    var hasRemoved = changes.some(function (c) { return c.type === "remove"; });
    if (hasMoved || hasRemoved) markDirty();
  }, [onNodesChange, markDirty]);

  var handleEdgesChange = useCallback(function (changes) {
    onEdgesChange(changes);
    if (changes.some(function (c) { return c.type === "remove"; })) markDirty();
  }, [onEdgesChange, markDirty]);

  var handleManualSave = function () {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    doSave(nodes, edges, title, isActive);
  };

  var handleToggleActive = function () {
    setIsActive(function (prev) { return !prev; });
    markDirty();
  };

  var onNodeClick = useCallback(function (_event, node) {
    setSelectedNodeId(node.id);
  }, []);

  var onPaneClick = useCallback(function () {
    setSelectedNodeId(null);
  }, []);

  var memoNodeTypes = useMemo(function () { return nodeTypes; }, []);

  // ====================================================================
  // Drag da borda direita da sidebar — implementação "uncontrolled"
  // durante o arraste: mutamos `ref.current.style.width` direto no DOM
  // a cada mousemove, sem re-render do React. Só fazemos setState +
  // localStorage no mouseup. Isso evita travar o react-flow com dezenas
  // de re-renders por segundo.
  // ====================================================================
  var onSidebarDragMove = useCallback(function (e) {
    if (!draggingRef.current || !sidebarRef.current) return;
    var delta = e.clientX - dragStartRef.current.x;
    var next = dragStartRef.current.w + delta;
    if (next < SIDEBAR_MIN_WIDTH) next = SIDEBAR_MIN_WIDTH;
    if (next > SIDEBAR_MAX_WIDTH) next = SIDEBAR_MAX_WIDTH;
    sidebarRef.current.style.width = next + "px";
    // Log panel acompanha em tempo real (também via DOM direto, sem re-render).
    if (logPanelRef.current) {
      logPanelRef.current.style.left = (next + 4) + "px";
    }
  }, []);

  var onSidebarDragEnd = useCallback(function () {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    window.removeEventListener("mousemove", onSidebarDragMove);
    window.removeEventListener("mouseup", onSidebarDragEnd);
    // Lê a largura final direto do DOM e sincroniza state + localStorage.
    if (sidebarRef.current) {
      var finalWidth = sidebarRef.current.offsetWidth;
      setSidebarWidth(finalWidth);
      try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(finalWidth)); } catch (e) { /* quota etc */ }
    }
  }, [onSidebarDragMove]);

  var onSidebarDragStart = useCallback(function (e) {
    if (!sidebarRef.current) return;
    e.preventDefault();
    draggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX,
      w: sidebarRef.current.offsetWidth,
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onSidebarDragMove);
    window.addEventListener("mouseup", onSidebarDragEnd);
  }, [onSidebarDragMove, onSidebarDragEnd]);

  var onSidebarDoubleClick = useCallback(function () {
    if (sidebarRef.current) sidebarRef.current.style.width = SIDEBAR_DEFAULT_WIDTH + "px";
    setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
    try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(SIDEBAR_DEFAULT_WIDTH)); } catch (e) { /* */ }
  }, []);

  return (
    <div className={classes.root}>
      {/* Topbar */}
      <div className={classes.topbar}>
        <Tooltip title="Voltar">
          <IconButton
            size="small"
            className={classes.backBtn}
            onClick={function () { history.push("/fluxo"); }}
          >
            <ArrowLeft size={18} />
          </IconButton>
        </Tooltip>

        <div className={classes.divider} />

        <input
          className={classes.titleInput}
          value={title}
          onChange={function (e) {
            setTitle(e.target.value);
            markDirty();
          }}
          placeholder="Nome do fluxo..."
        />

        <div className={classes.topbarRight}>
          <span
            className={classes.statusTag + " " + (isActive ? classes.activeTag : classes.inactiveTag)}
          >
            <Power size={10} />
            {isActive ? "Ativo" : "Inativo"}
          </span>

          <Switch
            size="small"
            checked={isActive}
            onChange={handleToggleActive}
            color="primary"
          />

          <div className={classes.divider} />

          <button
            className={classes.saveBtn + (saved ? " " + classes.savedBtn : "")}
            onClick={handleManualSave}
            disabled={saving || (!dirty && !saved)}
          >
            {saving ? (
              <CircularProgress size={14} style={{ color: "#fff" }} />
            ) : saved ? (
              <React.Fragment>
                <Check size={14} />
                Salvo
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Save size={14} />
                Salvar
              </React.Fragment>
            )}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className={classes.body}>
        {/* Sidebar — redimensionável via handle na borda direita */}
        <div
          ref={sidebarRef}
          className={classes.sidebar}
          style={{ width: sidebarWidth }}
        >
          <div className={classes.sidebarList}>
            {SIDEBAR_CATEGORIES.map(function (cat) {
              return (
                <React.Fragment key={cat.label}>
                  <div className={classes.sidebarTitle}>{cat.label}</div>
                  {cat.items.map(function (cfg) {
                    var Icon = cfg.icon;
                    return (
                      <div
                        key={cfg.type}
                        className={classes.nodeType}
                        draggable
                        onDragStart={function (e) { onDragStart(e, cfg.type); }}
                      >
                        <div
                          className={classes.nodeTypeIcon}
                          style={{ background: cfg.color + "18" }}
                        >
                          <Icon size={14} color={cfg.color} strokeWidth={2} />
                        </div>
                        <div className={classes.nodeTypeInfo}>
                          <div className={classes.nodeTypeLabel}>{cfg.label}</div>
                          <div className={classes.nodeTypeDesc}>{cfg.desc}</div>
                        </div>
                        <GripVertical
                          size={12}
                          className={classes.nodeTypeGrip}
                        />
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Handle para redimensionar a sidebar (drag horizontal) */}
        <div
          className={classes.sidebarResizeHandle}
          onMouseDown={onSidebarDragStart}
          onDoubleClick={onSidebarDoubleClick}
          title="Arraste para redimensionar. Duplo clique para restaurar."
          role="separator"
          aria-orientation="vertical"
        />

        {/* Canvas */}
        <div
          className={classes.canvas}
          ref={reactFlowWrapper}
          style={{ "--log-panel-offset": ((logPanelOpen ? 220 : 36) + 12) + "px" }}
        >
          {loading && (
            <div className={classes.loadingOverlay}>
              <CircularProgress size={28} style={{ color: "#5B7CFF" }} />
            </div>
          )}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={memoNodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            defaultEdgeOptions={{
              type: "smoothstep",
              animated: true,
              markerEnd: { type: MarkerType.ArrowClosed, color: "#5B7CFF" },
              style: { stroke: "#5B7CFF", strokeWidth: 2 },
            }}
            deleteKeyCode="Backspace"
            snapToGrid
            snapGrid={[16, 16]}
          >
            <Background
              variant="dots"
              gap={20}
              size={1}
              color="rgba(91, 124, 255, 0.12)"
            />
            <Controls showInteractive={false} />
            <MiniMap
              nodeStrokeWidth={3}
              nodeColor="#5B7CFF"
              nodeBorderRadius={8}
              maskColor="rgba(0, 0, 0, 0.4)"
              zoomable
              pannable
              style={{ width: 140, height: 100, backgroundColor: "#0e1017" }}
            />
          </ReactFlow>
        </div>

        {/* Config Panel (right) */}
        <ConfigPanel
          classes={classes}
          selectedNode={selectedNode}
          setSelectedNodeId={setSelectedNodeId}
          updateNodeData={updateNodeData}
          queues={queues}
          whatsapps={whatsapps}
          setPromptModalOpen={setPromptModalOpen}
          fileUploading={fileUploading}
          setFileUploading={setFileUploading}
          pendingFileName={pendingFileName}
          setPendingFileName={setPendingFileName}
          pendingFile={pendingFile}
          setPendingFile={setPendingFile}
          toastError={toastError}
          api={api}
          memName={memName}
          setMemName={setMemName}
          memContent={memContent}
          setMemContent={setMemContent}
          memSummary={memSummary}
          setMemSummary={setMemSummary}
          memGenerating={memGenerating}
          setMemGenerating={setMemGenerating}
          memEditingId={memEditingId}
          setMemEditingId={setMemEditingId}
          memExpandedId={memExpandedId}
          setMemExpandedId={setMemExpandedId}
          leadStatuses={leadStatuses}
          setLeadStatuses={setLeadStatuses}
          crmBoards={crmBoards}
          crmFieldName={crmFieldName}
          setCrmFieldName={setCrmFieldName}
          memPromptModalOpen={memPromptModalOpen}
          setMemPromptModalOpen={setMemPromptModalOpen}
          edges={edges}
          nodes={nodes}
          bulkTemplates={bulkTemplates}
          loadingTemplates={loadingTemplates}
          bulkSending={bulkSending}
          bulkProgress={bulkProgress}
          handleBulkSend={handleBulkSend}
          users={users}
          wooIntegrations={wooIntegrations}
        />
      </div>

      {/* Log Panel — segue a largura da sidebar (sidebarWidth + 4px do handle) */}
      <LogPanel
        classes={classes}
        logPanelOpen={logPanelOpen}
        setLogPanelOpen={setLogPanelOpen}
        executionLogs={executionLogs}
        nodes={nodes}
        leftOffset={sidebarWidth + 4}
        panelRef={logPanelRef}
      />

      {/* Prompt expand modal */}
      {promptModalOpen && (
        <PromptModal
          classes={classes}
          selectedNode={selectedNode}
          edges={edges}
          nodes={nodes}
          updateNodeData={updateNodeData}
          setPromptModalOpen={setPromptModalOpen}
        />
      )}

      {/* Memory content expand modal */}
      {memPromptModalOpen && (
        <MemoryPromptModal
          classes={classes}
          selectedNode={selectedNode}
          edges={edges}
          nodes={nodes}
          memContent={memContent}
          setMemContent={setMemContent}
          setMemPromptModalOpen={setMemPromptModalOpen}
        />
      )}
    </div>
  );
};

/* ─── Wrapper with Provider ──────────────────────────── */
var FlowBuilder = function () {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner />
    </ReactFlowProvider>
  );
};

export default FlowBuilder;
